import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

interface SignupForm {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
}

interface ConfirmForm {
  code: string;
}

const SignupPage: React.FC = () => {
  const { signup, confirmSignup } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnUrl = searchParams.get('returnUrl');
  const [error, setError] = useState<string | null>(null);
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);

  const {
    register: registerSignup,
    handleSubmit: handleSignupSubmit,
    formState: { errors: signupErrors, isSubmitting: isSigningUp },
    watch,
  } = useForm<SignupForm>();

  const {
    register: registerConfirm,
    handleSubmit: handleConfirmSubmit,
    formState: { errors: confirmErrors, isSubmitting: isConfirming },
  } = useForm<ConfirmForm>();

  const password = watch('password');

  const onSignup = async (data: SignupForm) => {
    setError(null);
    try {
      await signup(data.email, data.password, data.name);
      setPendingEmail(data.email);
    } catch (err: any) {
      setError(err.message || 'Signup failed');
    }
  };

  const onConfirm = async (data: ConfirmForm) => {
    setError(null);
    if (!pendingEmail) return;
    try {
      await confirmSignup(pendingEmail, data.code);
      navigate(returnUrl ? `/login?returnUrl=${encodeURIComponent(returnUrl)}` : '/login', { replace: true });
    } catch (err: any) {
      setError(err.message || 'Confirmation failed');
    }
  };

  if (pendingEmail) {
    return (
      <div className="main-content">
        <div className="auth-page">
          <div className="form-container auth-form-container">
            <h2>Confirm Your Email</h2>
            <p className="auth-subtitle">We sent a verification code to <strong>{pendingEmail}</strong></p>
            {error && <p className="auth-error">{error}</p>}
            <form onSubmit={handleConfirmSubmit(onConfirm)} className="admin-form">
              <div className="form-group">
                <label htmlFor="code">Verification Code</label>
                <input
                  id="code"
                  type="text"
                  className={confirmErrors.code ? 'error' : ''}
                  {...registerConfirm('code', { required: 'Verification code is required' })}
                />
                {confirmErrors.code && <span className="error-text">{confirmErrors.code.message}</span>}
              </div>
              <div className="form-actions">
                <button type="submit" className="btn btn-primary" disabled={isConfirming}>
                  {isConfirming ? 'Confirming...' : 'Confirm'}
                </button>
              </div>
            </form>
            <div className="auth-links">
              <p><Link to={returnUrl ? `/login?returnUrl=${encodeURIComponent(returnUrl)}` : '/login'}>Back to login</Link></p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="main-content">
      <div className="auth-page">
        <div className="form-container auth-form-container">
          <h2>Sign Up</h2>
          {error && <p className="auth-error">{error}</p>}
          <form onSubmit={handleSignupSubmit(onSignup)} className="admin-form">
            <div className="form-group">
              <label htmlFor="name">Name</label>
              <input
                id="name"
                type="text"
                className={signupErrors.name ? 'error' : ''}
                {...registerSignup('name', { required: 'Name is required' })}
              />
              {signupErrors.name && <span className="error-text">{signupErrors.name.message}</span>}
            </div>
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                className={signupErrors.email ? 'error' : ''}
                {...registerSignup('email', { required: 'Email is required' })}
              />
              {signupErrors.email && <span className="error-text">{signupErrors.email.message}</span>}
            </div>
            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                id="password"
                type="password"
                className={signupErrors.password ? 'error' : ''}
                {...registerSignup('password', {
                  required: 'Password is required',
                  minLength: { value: 8, message: 'Password must be at least 8 characters' },
                  validate: (value) => {
                    if (!/[A-Z]/.test(value)) return 'Must contain an uppercase letter';
                    if (!/[a-z]/.test(value)) return 'Must contain a lowercase letter';
                    if (!/[0-9]/.test(value)) return 'Must contain a number';
                    return true;
                  },
                })}
              />
              {signupErrors.password && <span className="error-text">{signupErrors.password.message}</span>}
              <span className="form-help">8+ characters, uppercase, lowercase, and a number</span>
            </div>
            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <input
                id="confirmPassword"
                type="password"
                className={signupErrors.confirmPassword ? 'error' : ''}
                {...registerSignup('confirmPassword', {
                  required: 'Please confirm your password',
                  validate: (value) => value === password || 'Passwords do not match',
                })}
              />
              {signupErrors.confirmPassword && (
                <span className="error-text">{signupErrors.confirmPassword.message}</span>
              )}
            </div>
            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={isSigningUp}>
                {isSigningUp ? 'Signing up...' : 'Sign Up'}
              </button>
            </div>
          </form>
          <div className="auth-links">
            <p>Already have an account? <Link to={returnUrl ? `/login?returnUrl=${encodeURIComponent(returnUrl)}` : '/login'}>Log in</Link></p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;
